import { useState } from "react";
import { Bot, ChevronRight, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { AgentsSkeleton } from "@/components/PageSkeletons";

function fmtLatency(ms) {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
function fmtSuccess(r) {
  if (r == null) return "—";
  return `${Math.round(r * 100)}%`;
}
function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function AgentCard({ agent, onOpen }) {
  const s = agent.stats;
  const deprecated = agent.category === "deprecated";
  return (
    <button
      data-testid={`agent-card-${agent.name}`}
      onClick={() => onOpen(agent)}
      className="text-left hm-surface hover:border-hm-border-strong transition-colors p-5 flex flex-col gap-4 group"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-hm-bg-2 border border-hm-border-strong flex items-center justify-center">
          <Bot className="h-5 w-5 text-hm-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-hm-fg">{agent.name}</span>
            {deprecated && (
              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-hm-warn/15 text-hm-warn">
                deprecated
              </span>
            )}
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">
            {agent.provider} · {agent.model}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-hm-fg-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="text-xs text-hm-fg-muted leading-relaxed line-clamp-2">
        {agent.description}
      </p>
      <div className="grid grid-cols-3 gap-2 mt-auto pt-3 border-t border-hm-border">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">runs 7d</div>
          <div className="mono text-sm text-hm-fg">{s.runs}</div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">avg</div>
          <div className="mono text-sm text-hm-fg">{fmtLatency(s.avg_latency_ms)}</div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">ok</div>
          <div className="mono text-sm text-hm-fg">{fmtSuccess(s.success_rate)}</div>
        </div>
      </div>
      <div className="mono text-[10px] text-hm-fg-subtle">last: {timeAgo(s.last_seen)}</div>
    </button>
  );
}

function AgentDrawer({ agent, onClose }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    if (!agent) return;
    setRuns(null);
    (async () => {
      try {
        const res = await fetch(`${API}/agents/${encodeURIComponent(agent.name)}/runs?limit=15`, {
          headers: authHeaders(),
        });
        if (res.ok) setRuns(await res.json());
        else setRuns([]);
      } catch {
        setRuns([]);
      }
    })();
  }, [agent]);
  if (!agent) return null;
  return (
    <div
      className="fixed inset-0 z-30 bg-[var(--hm-overlay)] backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="agent-drawer"
    >
      <aside
        className="w-full max-w-[560px] h-full bg-hm-bg-2 border-l border-hm-border-strong shadow-hm-glow flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="h-14 flex items-center gap-3 px-5 border-b border-hm-border shrink-0">
          <Bot className="h-4 w-4 text-hm-accent" />
          <div>
            <div className="text-sm font-medium text-hm-fg">{agent.name}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              {agent.provider} · {agent.model}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          <p className="text-sm text-hm-fg-muted">{agent.description}</p>
          <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle mt-4">
            recent runs
          </div>
          {runs === null && (
            <div className="mono text-[11px] text-hm-fg-subtle">loading…</div>
          )}
          {runs && runs.length === 0 && (
            <div className="mono text-[11px] text-hm-fg-subtle">no runs yet</div>
          )}
          {runs && runs.map((r) => (
            <div
              key={r.id}
              className="hm-surface p-3 mono text-[11px]"
              data-testid={`agent-run-${r.id}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className={
                    "h-1.5 w-1.5 rounded-full " +
                    (r.level === "error" ? "bg-hm-danger" : "bg-hm-accent")
                  }
                />
                <span className="text-hm-fg">{r.message || "—"}</span>
                {r.latency_ms != null && (
                  <span className="ml-auto text-hm-fg-subtle">{fmtLatency(r.latency_ms)}</span>
                )}
              </div>
              <div className="text-hm-fg-subtle">
                run <span className="text-hm-fg-muted">{(r.run_id || "").slice(0, 8)}</span> · {timeAgo(r.ts)}
              </div>
              {r.payload && Object.keys(r.payload).length > 0 && (
                <pre className="mt-2 whitespace-pre-wrap break-words text-hm-fg-muted max-h-40 overflow-y-auto">
                  {typeof r.payload === "string" ? r.payload : JSON.stringify(r.payload, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default function AgentsPage() {
  const [selected, setSelected] = useState(null);
  const { data: agents, isPending } = useQuery({
    queryKey: qk.agents(),
    queryFn: fetchers.agents,
    refetchInterval: 10_000,
  });

  return (
    <div data-testid="agents-page">
      <PageHeader
        title="Agents"
        badge="Live"
        subtitle="Every specialised agent, their runs, traces, health."
      />
      {isPending || !agents ? (
        <AgentsSkeleton n={9} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agents-grid">
          {agents.map((a) => (
            <AgentCard key={a.name} agent={a} onOpen={setSelected} />
          ))}
        </div>
      )}
      <AgentDrawer agent={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
